/* « Démarrer » — the guide of the one-location product (D-49, 05/09 night): six steps, in
   the order the merchant meets them, one question per step, defaults pre-filled, never a
   blocking step. Steps 1–4 live here, on the walkable set of record (parcours-marchand-v2;
   merchant-app review MA-5): 1 GuideEtablissement — the sector, the fiche and the brand (FQ-C4);
   2 « Vos plateformes »; 3 GuideCadeaux — one card per gift; 4 GuideRoue — the short roue editor.
   Step 5 is the campaign screen in guided mode; step 6 the activation screen (the QR and the test).
   « Enregistrer et quitter » at any step — everything is saved as you go; Accueil shows the progress.
   v3.3 migration · pack accueil (WF-002 · WF-052): « Campagne suivie par ce guide » — a select over the campaigns the app
   knows (empty « Choisir une campagne » while none exists); chosen, it lists what that campaign uses, each row a door to
   relire. Guidance only (Orchestrator ruling): no acknowledgement ledger, nothing gates a step, « Publier » or a QR.
   Wireframes-fully ruling 20/10 § 1 (campaign-first): this guide is no longer the first run — Accueil's day one opens the
   builder; the six steps stay here as the « Guide Démarrer » rows, from Accueil's header. With no campaign yet the select's
   card offers the same door as Accueil (« Créer ma première campagne », the builder at step 1, not guided). */
import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Select, Skeleton } from '@/ui'
import { useCampaigns, useLocations } from '@/lib/api'
import { allCampaigns, getCampaign, getDestinations, platformLabel } from '@/lib/campaign'
import { GUIDE_STEPS } from '@/lib/vocab'
import GuideEtablissement from './GuideEtablissement'
import GuideCadeaux from './GuideCadeaux'
import GuideRoue from './GuideRoue'
import { LiensPanel } from '@/routes/parametres/Plateformes'

/** The progress strip of the guide — also shown on the campaign screen (step 5) and the activation (step 6) when ?guide=1. */
export function GuideBar({ current, onQuit }: { current: number; onQuit?: () => void }) {
  const nav = useNavigate()
  return (
    <div className="guide-bar">
      <div className="guide-steps" role="list" aria-label="Étapes du guide">
        {GUIDE_STEPS.map((s, i) => {
          const n = i + 1
          const state = n < current ? 'done' : n === current ? 'cur' : ''
          return <button key={s.short} type="button" role="listitem" className={'guide-st ' + state} onClick={() => { if (n < current) nav(`/guide?etape=${n}`) }} disabled={n > current} aria-current={n === current ? 'step' : undefined}><i>{n < current ? '✓' : n}</i> {s.short}</button>
        })}
      </div>
      <a href="#" className="f-help" onClick={e => { e.preventDefault(); if (onQuit) onQuit(); else nav('/') }}>Enregistrer et quitter</a>
    </div>
  )
}

/** WF-002 / WF-052 · the campaign this guide follows, and what it uses — rows with a door each, never a check. */
function CampagneSuivie({ locId, locations }: { locId: string; locations: { id: string; name: string }[] }) {
  const [params, setParams] = useSearchParams()
  const nav = useNavigate()
  const { data: apiCampaigns } = useCampaigns()
  const list = allCampaigns(apiCampaigns, Object.fromEntries(locations.map(l => [l.id, l.name]))).filter(c => c.locations.includes(locId))
  const chosenId = params.get('campagne') || ''
  const chosen = list.find(c => c.id === chosenId)
  const choose = (id: string) => { const p = new URLSearchParams(params); if (id) p.set('campagne', id); else p.delete('campagne'); setParams(p, { replace: true }) }
  const own = chosen ? getCampaign(chosen.id) : undefined
  const dests = getDestinations().filter(x => x.locationId === locId && x.state === 'validee')
  const go = (to: string) => (e: React.MouseEvent) => { e.preventDefault(); nav(to) }
  const row = (title: string, line: string, doors: { label: string; to: string }[]) => (
    <li style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', padding: '8px 0', borderTop: '1px solid var(--bmr-border)' }}>
      <span aria-hidden className="f-help">→</span>
      <span style={{ flex: '1 1 240px', minWidth: 0 }}><b>{title}</b><br /><span className="f-help">{line}</span></span>
      <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{doors.map(d => <Button key={d.label} size="sm" onClick={go(d.to)}>{d.label}</Button>)}</span>
    </li>
  )
  return (
    <section className="card" data-testid="campagne-suivie" style={{ margin: '0 0 18px', maxWidth: 760 }}>
      <label className="f-label" style={{ margin: 0, display: 'block' }}>Campagne suivie par ce guide
        <Select aria-label="Campagne suivie par ce guide" value={chosen ? chosen.id : ''} onChange={e => choose(e.target.value)} disabled={list.length === 0} style={{ marginTop: 6 }}>
          <option value="">Choisir une campagne</option>
          {list.map(c => <option key={c.id} value={c.id}>{c.name || 'Sans nom'}</option>)}
        </Select>
      </label>
      {list.length === 0 ? (
        <>
          <p className="f-help" style={{ margin: '8px 0 0' }}>Aucune campagne pour l’instant&nbsp;: le guide la prépare à l’étape 5. Les étapes 1 à 4 préparent ce qu’elle utilisera.</p>
          <p className="f-help" style={{ margin: '8px 0 0', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>Ou commencez par elle&nbsp;: ce qui lui manque vous est proposé en chemin.
            <Button size="sm" data-testid="guide-creer-campagne" onClick={() => nav('/campagnes/nouvelle?reset=1')}>Créer ma première campagne</Button></p>
        </>
      ) : !chosen ? (
        <p className="f-help" style={{ margin: '8px 0 0' }}>Choisissez la campagne, puis vérifiez uniquement ce qu’elle utilise.</p>
      ) : (
        <>
          <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0 }}>
            {row('Votre campagne', chosen.status === 'draft' ? 'Préparez les trois étapes, testez puis publiez la version choisie.' : `« ${chosen.name} » · version ${chosen.version}.`, [{ label: 'Revoir', to: chosen.own ? (chosen.status === 'draft' ? `/campagnes/nouvelle?brouillon=${chosen.id}` : `/campagnes/${chosen.id}`) : `/campagnes/${chosen.id}` }])}
            {row(own && !own.recipe.roueId && !own.recipe.giftTypeId ? 'Cadeaux de cette campagne' : 'Cadeaux et roue de cette campagne',
              own && !own.recipe.roueId && !own.recipe.giftTypeId ? 'Cette campagne n’offre pas de cadeau — le parcours vaut avec ou sans.' : 'Relisez les contenus réellement utilisés par les visites.',
              own && !own.recipe.roueId && !own.recipe.giftTypeId ? [] : [{ label: 'Relire les cadeaux', to: '/cadeaux' }, ...(own?.recipe.roueId ? [{ label: 'Relire la roue', to: '/cadeaux?onglet=roues' }] : [])])}
            {row('Plateformes de cette campagne', dests.length ? `${dests.map(x => platformLabel(x.platform)).join(', ')} · ouvrez chaque plateforme pour vérifier le salon et la page affichée.` : 'Aucune plateforme liée pour l’instant — l’étape 2 la lie.',
              [...dests.filter(x => !!x.url).map(x => ({ label: `Vérifier ${platformLabel(x.platform)}`, to: '/parametres/plateformes' })), { label: 'Relire', to: '/parametres/plateformes' }])}
            {row('Votre support et votre test', 'Vérifiez le parcours publié, chaque accès et sa mise en place — un test guide, il ne bloque rien.', [{ label: 'Continuer', to: '/supports' }])}
          </ul>
          <p className="f-help" style={{ margin: '10px 0 0' }}>Les vérifications concernent cette campagne et ces versions de contenus. Elles ne sont pas transférées à une autre campagne.</p>
        </>
      )}
    </section>
  )
}

export default function Guide() {
  const [params, setParams] = useSearchParams()
  const nav = useNavigate()
  const { data: locations, isPending } = useLocations()
  const etape = Math.min(6, Math.max(1, Number(params.get('etape') || 1)))
  const go = (n: number) => { const p = new URLSearchParams(params); p.set('etape', String(n)); setParams(p) }
  /* step 5 and 6 are other screens in guided mode */
  useEffect(() => {
    if (etape === 5) nav('/campagnes/nouvelle?guide=1&reset=1', { replace: true }) // D-64 · the prefilled stepper is step 5
    if (etape === 6) nav('/?guide=done', { replace: true })
  }, [etape, nav])

  if (isPending) return <div style={{ display: 'grid', gap: 10, maxWidth: 720 }}><Skeleton width="42%" /><Skeleton /><Skeleton width="65%" /></div>
  const loc = (locations ?? []).find(l => l.status !== 'inactive') ?? (locations ?? [])[0]
  if (!loc) return null

  return (
    <div className="page guide">
      <GuideBar current={etape} />
      <CampagneSuivie locId={loc.id} locations={locations ?? []} />
      {etape === 1 && <GuideEtablissement loc={loc} onNext={() => go(2)} />}
      {etape === 2 && (
        <div data-testid="guide-plateformes" style={{ maxWidth: 760 }}>
          <p className="t-eyebrow" style={{ margin: '0 0 4px' }}>Démarrer · étape 2 sur 6</p>
          <h2 className="t-page" style={{ margin: '0 0 4px' }}>{GUIDE_STEPS[1].title}</h2>
          <p className="t-body" style={{ margin: '0 0 20px', maxWidth: 680 }}>Où vos clients laissent-ils leur avis&nbsp;? Google vient de votre fiche. Les autres sont facultatives&nbsp;: vous pourrez les ajouter plus tard dans Paramètres.</p>
          <LiensPanel locId={loc.id} locName={loc.name} guide />
          <div className="actions" style={{ marginTop: 20 }}>
            <Button variant="quiet" onClick={() => go(1)}>‹ Retour</Button>
            <Button variant="primary" onClick={() => go(3)} data-testid="guide-suivant-2">Suivant&nbsp;: vos cadeaux →</Button>
          </div>
        </div>
      )}
      {etape === 3 && <GuideCadeaux loc={loc} onBack={() => go(2)} onNext={() => go(4)} />}
      {etape === 4 && <GuideRoue onBack={() => go(3)} onNext={() => go(5)} />}
    </div>
  )
}
